// @ts-check
// pwa/components/profile-sheet.js — profile slide-in sheet (spec 003).
//
// Sections: identity · Roles & currency · Contact details · Calendar sync ·
// Email & notifications · Season commitment · quick-start · version · Sign out ·
// self soft-delete. Lazy data: GET /api/me/calendar + GET /api/me/profile.
//
// Render pattern (avoids the lit-html append-duplicate bug): each lazy section's
// HEAD is static in the template and never re-rendered; only the [data-*-body]
// child is lit-rendered, and it's cleared once before the first paint.

import { html, render } from 'lit-html';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { icon } from '../lib/icons.js';
import { api } from '../lib/api.js';
import { $session } from '../lib/store.js';
import { getTheme, setTheme } from '../lib/theme.js';
import {
  isPushSupported, isInstalled, isIOS, subscribe, unsubscribePush, requestAndSubscribe,
  getPushSubscriptionState,
} from '../lib/push-permission.js';

// Reminder lead-time presets (minutes before start) — must mirror the server's
// OFFSET_PRESETS in worker/src/handlers/me-notifications.js.
// Set updated 2026-05-31: 1 hour / 1 day / 3 days / 1 week / 2 weeks.
// All presets are independently selectable (no sub-cap); the server's MAX_OFFSETS
// equals the preset count so any combination — including all five — saves.
const REMINDER_PRESETS = [
  { m: 20160, label: '2 weeks' },
  { m: 10080, label: '1 week' },
  { m: 4320, label: '3 days' },
  { m: 1440, label: '1 day' },
  { m: 60, label: '1 hour' },
];

// Prefilled Chair mailto links. Club domain = ncslsc.com (canonical, matches the
// email module's reply_to). The mockup's .org.au was wrong.
const QUALS_MAILTO = 'mailto:education@ncslsc.com?subject=Role%20%2F%20qualification%20correction%20request&body=Hi%20Chair%2C%0A%0AThe%20following%20looks%20wrong%20on%20my%20profile%3A%0A%0A%5BRole%20or%20qual%5D%3A%20%0A%5BWhat%20should%20it%20be%5D%3A%20%0A%5BAny%20evidence%2Fcontext%5D%3A%20%0A%0AThanks.';

// Trigger the prefilled mail to the Chair from a user gesture. Driving the mailto
// programmatically (rather than relying on an <a> default) sidesteps SPA/PWA anchor
// edge cases; the browser hands off to the OS mail handler without navigating away.
function openQualsMail() { window.location.href = QUALS_MAILTO; }

// Lazy-loaded state for the open sheet.
// Fields mirror the PATCH /api/me/notifications payload (patch-019 + patch-020).
let NOTIF = {
  notify_enabled: true,
  reminder_offsets: [],
  nudges_enabled: true,
  // Email channel toggles (patch-018)
  spot_alerts_enabled: true,
  // In-app channel toggles (patch-019)
  spot_alerts_inapp_enabled: true,
  interest_alerts_inapp_enabled: true,
  // Admin-only channel toggles (patch-019)
  admin_interest_email_enabled: true,
  admin_interest_inapp_enabled: true,
  // Master push switch (patch-020)
  push_notifications_enabled: true,
};
// Contact (self-editable mobile + notification email). status: loading | ready | error.
// editField: 'mobile' | 'contact_email' | null — which field is being edited.
// _draft: unsaved value currently in the notification email input (used for migration request button).
let CONTACT = { mobile: null, contact_email: null, pending_login_email: null, login_email: null, status: 'loading', editField: null, saving: false, error: '', _draft: null };

/**
 * Validate + normalise an Australian phone (mirrors the worker's normaliseAuPhone).
 * Mobile 04xx xxx xxx or landline 0[2378]xx xxx xxx; +61 forms normalise to leading 0.
 * @returns {{ ok: true, value: string|null } | { ok: false, message: string }}
 */
function normaliseAuPhone(input) {
  if (input == null || String(input).trim() === '') return { ok: true, value: null };
  let s = String(input).replace(/[\s()\-.]/g, '');
  if (s.startsWith('+61')) s = '0' + s.slice(3);
  else if (s.startsWith('61') && s.length === 11) s = '0' + s.slice(2);
  if (!/^0[234578]\d{8}$/.test(s)) {
    return { ok: false, message: 'Enter a valid Australian number — a mobile (04xx xxx xxx) or landline (e.g. 08 xxxx xxxx).' };
  }
  return { ok: true, value: s };
}
/** Pretty-print a stored AU number: mobile → 04xx xxx xxx, landline → 0x xxxx xxxx. */
function fmtPhone(s) {
  if (!s) return '';
  if (/^04\d{8}$/.test(s)) return `${s.slice(0, 4)} ${s.slice(4, 7)} ${s.slice(7)}`;
  if (/^0[2378]\d{8}$/.test(s)) return `${s.slice(0, 2)} ${s.slice(2, 6)} ${s.slice(6)}`;
  return s;
}

/** Two-letter initials from preferred name (fallback first/last/email → '?'). */
export function userInitials(me) {
  const name = me?.preferred_name || me?.first_name || me?.email || '';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] || '';
  const b = parts[1]?.[0] || me?.last_name?.[0] || '';
  return (a + b).toUpperCase() || '?';
}

const ic = (n, c = 'w-3.5 h-3.5') => unsafeHTML(icon(n, c));

export function profileSheetTemplate(me) {
  const name = me?.preferred_name || me?.first_name || 'Your profile';
  return html`
    <div class="sheet-backdrop" data-profile-backdrop></div>
    <aside class="sheet" id="profile-sheet" role="dialog" aria-modal="true"
           aria-labelledby="profile-sheet-title" aria-hidden="true" ?inert=${true}>
      <div class="sheet__head">
        <h2 id="profile-sheet-title">Your profile</h2>
        <button type="button" class="sheet__close" data-profile-close aria-label="Close">
          ${ic('x', 'w-5 h-5')}
        </button>
      </div>
      <div class="sheet__body">
        <div class="sheet__identity">
          <span class="sheet__avatar-lg" data-user-initials>${userInitials(me)}</span>
          <div class="sheet__identity-text">
            <div class="sheet__name" data-user-name>${name}</div>
            <div class="sheet__email" data-user-memberid hidden></div>
            <div class="sheet__email" data-user-email>${me?.email || ''}</div>
          </div>
        </div>

        <section class="sheet__section" data-roles-section>
          <div class="sheet__section-head">
            <span class="sheet__section-head-label">${ic('award')} Roles &amp; currency</span>
            <button type="button" class="sheet__section-cta" @click=${openQualsMail}>
              <span class="sheet__section-cta-text">Something wrong?</span>${ic('arrow-right', 'w-3 h-3')}
            </button>
          </div>
          <div data-roles-body></div>
        </section>

        <section class="sheet__section" data-contact-section>
          <div data-contact-body></div>
        </section>

        <section class="sheet__section" data-cal-section>
          <div class="sheet__section-head"><span class="sheet__section-head-label">${ic('calendar-plus')} Calendar sync</span></div>
          <div data-cal-body></div>
        </section>

        <section class="sheet__section" data-notif-section>
          <div class="sheet__section-head"><span class="sheet__section-head-label">${ic('bell-ring')} Email &amp; notifications</span></div>
          <div data-notif-body></div>
        </section>

        <section class="sheet__section" data-history-section>
          <div class="sheet__section-head"><span class="sheet__section-head-label">${ic('history')} My sessions &amp; hours</span></div>
          <a href="/history" class="sheet__season" data-nav data-history-link>
            <span class="sheet__season-line">Your taught history, hours &amp; attendance</span>
            <span class="sheet__season-chev" aria-hidden="true">${ic('arrow-right', 'w-4 h-4')}</span>
          </a>
        </section>

        <section class="sheet__section" data-season-section>
          <div class="sheet__section-head"><span class="sheet__section-head-label">${ic('calendar-clock')} Season commitment</span></div>
          <a href="/#commitment" class="sheet__season" data-commit-detail>
            <span class="sheet__season-line" data-season-summary></span>
            <span class="sheet__season-chev" aria-hidden="true">${ic('arrow-right', 'w-4 h-4')}</span>
          </a>
        </section>

        <section class="sheet__section" data-appearance-section>
          <div class="sheet__section-head"><span class="sheet__section-head-label">${ic('sun-moon')} Appearance</span></div>
          <div class="sheet__pref-row sheet__pref-row--dual">
            <div class="sheet__pref-label">Theme<small>How the app looks on this device</small></div>
            <div data-theme-body></div>
          </div>
        </section>

        <a class="sheet__hint link-surf-blue" href="/help/starter" target="_blank" rel="noopener"
           style="display:inline-flex;align-items:center;gap:.4rem;justify-content:center;font-weight:600;text-decoration:none;">
          ${ic('book-open', 'w-4 h-4')} Quick start guide
        </a>
        <a class="sheet__hint link-surf-blue" href="/help/privacy" target="_blank" rel="noopener"
           style="display:inline-flex;align-items:center;gap:.4rem;justify-content:center;font-weight:600;text-decoration:none;">
          ${ic('lock', 'w-4 h-4')} Privacy
        </a>
        <div class="sheet__version" data-hub-version>NCSLSC TAF Hub v1.0</div>
      </div>
      <div class="sheet__foot">
        <button type="button" class="btn btn-destructive" id="header-signout" data-signout-trigger>
          ${ic('log-out', 'w-4 h-4')} Sign out
        </button>
        <button type="button" class="sheet__danger" data-offboard-open>
          ${ic('user-x', 'w-4 h-4')} Deactivate my account
        </button>
      </div>

      <div class="modal-backdrop" data-offboard-modal>
        <div class="modal sheet__danger-modal" role="dialog" aria-modal="true" aria-labelledby="offboard-title">
          <h3 id="offboard-title" class="modal__title">Deactivate your account?</h3>
          <div class="modal__body">
            This soft-deletes your account — an admin can restore it. You can't deactivate while
            you're signed up for upcoming sessions; withdraw from those first.
            <label for="offboard-name">Type your name to confirm</label>
            <input id="offboard-name" type="text" autocomplete="off" data-offboard-name placeholder="${name}">
            <div class="sheet__danger-err" data-offboard-err></div>
          </div>
          <div class="modal__actions">
            <button type="button" class="btn btn-outline" data-offboard-cancel>Cancel</button>
            <button type="button" class="btn btn-destructive" data-offboard-confirm>Deactivate</button>
          </div>
        </div>
      </div>
    </aside>
  `;
}

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/** @param {HTMLElement} root  @param {{ onSignoutTrigger?: () => void }} opts */
export function wireProfileSheet(root, { onSignoutTrigger } = {}) {
  const backdrop = root.querySelector('[data-profile-backdrop]');
  const sheet = /** @type {(HTMLElement & { _open?: () => void, _close?: () => void })|null} */ (root.querySelector('#profile-sheet'));
  if (!backdrop || !sheet) return;
  const mainEl = () => document.getElementById('app-main');
  const onKey = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;
    // APG Dialog focus trap — keep Tab cycling inside the sheet while open so
    // focus can't walk to the (visually obscured) page behind the backdrop.
    // getClientRects() filters hidden controls; robust under position:fixed iOS.
    const f = /** @type {HTMLElement[]} */ (Array.from(sheet.querySelectorAll(FOCUSABLE)).filter((el) => el.getClientRects().length > 0));
    if (!f.length) return;
    const first = f[0]; const last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  function close() {
    sheet.classList.remove('is-open');
    backdrop.classList.remove('is-open');
    sheet.setAttribute('inert', '');
    sheet.setAttribute('aria-hidden', 'true');
    mainEl()?.removeAttribute('inert');
    document.removeEventListener('keydown', onKey);
  }
  sheet.querySelector('[data-profile-close]')?.addEventListener('click', close);
  backdrop.addEventListener('click', close);
  // F016: navigating to /history must dismiss the sheet so it doesn't float over
  // the new view (the router handles the actual navigation via data-nav).
  sheet.querySelector('[data-history-link]')?.addEventListener('click', close);
  sheet.querySelector('[data-signout-trigger]')?.addEventListener('click', () => onSignoutTrigger?.());
  wireOffboard(sheet);
  sheet._open = () => {
    sheet.classList.add('is-open');
    backdrop.classList.add('is-open');
    sheet.removeAttribute('inert');
    sheet.setAttribute('aria-hidden', 'false');
    mainEl()?.setAttribute('inert', '');   // background can't receive focus while open
    document.addEventListener('keydown', onKey);
    /** @type {HTMLElement|null} */ (sheet.querySelector('[data-profile-close]'))?.focus();
    loadProfileExtras(root);
  };
  sheet._close = close;
}

// ── Appearance (theme) ───────────────────────────────────────────────────────
function renderTheme(root) {
  const box = root.querySelector('[data-theme-body]');
  if (!box) return;
  const current = getTheme();
  const opts = [
    { val: 'light', label: 'Light' },
    { val: 'system', label: 'Auto' },
    { val: 'dark', label: 'Dark' },
  ];
  render(html`
    <div class="sheet__tristate" role="group" aria-label="App theme">
      ${opts.map((o) => html`
        <button type="button"
          class="sheet__tristate-opt ${current === o.val ? 'is-active' : ''}"
          aria-pressed=${current === o.val ? 'true' : 'false'}
          @click=${() => { setTheme(o.val); renderTheme(root); }}>${o.label}</button>`)}
    </div>
  `, box);
}

async function loadProfileExtras(root) {
  renderTheme(root);
  const calBody = root.querySelector('[data-cal-body]');
  const notifBody = root.querySelector('[data-notif-body]');
  const rolesBody = root.querySelector('[data-roles-body]');
  const seasonEl = root.querySelector('[data-season-summary]');
  // Render the loading state through lit so lit OWNS each container from the first
  // paint. The section bodies are empty in the template (no static placeholder),
  // so this never double-appends — and crucially it makes the fetch re-runnable:
  // the retry button can call loadProfileExtras again and lit just diffs the parts
  // it already owns (replaceChildren() used to orphan lit's cached part → blank UI).
  const loading = (msg) => html`<p class="sheet__hint">${msg}</p>`;
  if (calBody) render(loading('Loading your calendar link…'), calBody);
  if (notifBody) render(loading('Loading your settings…'), notifBody);
  if (rolesBody) render(loading('Loading your roles…'), rolesBody);
  if (seasonEl) render(html`Loading…`, seasonEl);
  CONTACT = { mobile: null, contact_email: null, pending_login_email: null, login_email: null, status: 'loading', editField: null, saving: false, error: '', _draft: null };
  renderContact(root);

  const [cal, profile, notifPrefs] = await Promise.allSettled([
    api.get('/api/me/calendar'),
    api.get('/api/me/profile'),
    api.get('/api/me/notifications'),
  ]);

  try {
    if (cal.status === 'fulfilled') {
      renderCalendar(calBody, cal.value);
    } else {
      if (calBody) render(loadError(root, "Couldn't load your calendar link."), calBody);
    }
  } catch (e) {
    console.error('profile-sheet: renderCalendar threw', e?.message);
    if (calBody) render(loadError(root, "Couldn't load your calendar link."), calBody);
  }

  // Notification prefs come from /api/me/notifications (patch-019 fields included).
  // Fall back to the calendar response if notifications call fails (backward compat).
  try {
    const prefsSource = notifPrefs.status === 'fulfilled' ? notifPrefs.value
      : cal.status === 'fulfilled' ? cal.value : null;
    if (prefsSource) {
      NOTIF = {
        notify_enabled: prefsSource.notify_enabled !== false,
        reminder_offsets: prefsSource.reminder_offsets || [],
        nudges_enabled: prefsSource.nudges_enabled !== false,
        spot_alerts_enabled: prefsSource.spot_alerts_enabled !== false,
        spot_alerts_inapp_enabled: prefsSource.spot_alerts_inapp_enabled !== false,
        interest_alerts_inapp_enabled: prefsSource.interest_alerts_inapp_enabled !== false,
        admin_interest_email_enabled: prefsSource.admin_interest_email_enabled !== false,
        admin_interest_inapp_enabled: prefsSource.admin_interest_inapp_enabled !== false,
        push_notifications_enabled: prefsSource.push_notifications_enabled !== false,
      };
      renderNotifications(notifBody);
    } else {
      if (notifBody) render(loadError(root, "Couldn't load your notification settings."), notifBody);
    }
  } catch (e) {
    console.error('profile-sheet: renderNotifications threw', e?.message);
    if (notifBody) render(loadError(root, "Couldn't load your notification settings."), notifBody);
  }

  try {
    if (profile.status === 'fulfilled') {
      const p = profile.value;
      renderRoles(rolesBody, p.roles || []);
      const midEl = root.querySelector('[data-user-memberid]');
      if (midEl) {
        if (p.surfguard_member_id) { midEl.textContent = `Surfguard ID ${p.surfguard_member_id}`; midEl.hidden = false; }
        else { midEl.textContent = ''; midEl.hidden = true; }
      }
      CONTACT = {
        mobile: p.mobile || null,
        contact_email: p.contact_email || null,
        pending_login_email: p.pending_login_email || null,
        login_email: null, // populated below from sheet me-email span
        status: 'ready',
        editField: null,
        saving: false,
        error: '',
        _draft: null,
      };
      // Capture the login email from the rendered identity block.
      const emailEl = root.querySelector('[data-user-email]');
      if (emailEl) CONTACT = { ...CONTACT, login_email: emailEl.textContent.trim() || null };
      renderContact(root);
      renderSeason(root, p.commitment);
    } else {
      if (rolesBody) render(loadError(root, "Couldn't load your roles."), rolesBody);
      CONTACT = { ...CONTACT, status: 'error', editField: null };
      renderContact(root);
      if (seasonEl) render(html`Unavailable`, seasonEl);
    }
  } catch (e) {
    console.error('profile-sheet: profile section threw', e?.message);
    CONTACT = { ...CONTACT, status: 'error', editField: null };
    renderContact(root);
  }
}

// ── Contact details (self-editable mobile + notification email) ──────────────
function renderContact(root) {
  const box = root.querySelector('[data-contact-body]');
  if (!box) return;
  const { mobile, contact_email, pending_login_email, login_email, status, editField, saving, error } = CONTACT;
  const canEdit = status === 'ready';
  const isEditingMobile = editField === 'mobile';
  const isEditingEmail = editField === 'contact_email';
  const head = html`
    <div class="sheet__section-head">
      <span class="sheet__section-head-label">${ic('id-card')} Contact details</span>
    </div>`;

  const mobileValue = status === 'loading' ? 'Loading…' : status === 'error' ? '—' : (mobile ? fmtPhone(mobile) : 'Not on file');
  const emailValue = status === 'loading' ? 'Loading…' : status === 'error' ? '—' : (contact_email || null);

  const mobileRow = isEditingMobile
    ? mobileEdit(root)
    : html`<div class="sheet__contact-row">
        ${ic('phone')}<span class="sheet__contact-label">Phone</span>
        <span class="sheet__contact-value">${mobileValue}</span>
        ${canEdit ? html`<button type="button" class="sheet__contact-edit-btn" @click=${() => { CONTACT = { ...CONTACT, editField: 'mobile', error: '' }; renderContact(root); requestAnimationFrame(() => root.querySelector('#me-mobile')?.focus()); }}>
          ${ic('pencil', 'w-3.5 h-3.5')}</button>` : ''}
      </div>`;

  const emailRow = isEditingEmail
    ? contactEmailEdit(root)
    : html`<div class="sheet__contact-row">
        ${ic('at-sign')}<span class="sheet__contact-label">Notification email</span>
        ${emailValue
          ? html`<span class="sheet__contact-value">${emailValue}</span>`
          : html`<span class="sheet__contact-value" style="opacity:0.5">Same as login</span>`}
        ${canEdit ? html`<button type="button" class="sheet__contact-edit-btn" @click=${() => { CONTACT = { ...CONTACT, editField: 'contact_email', error: '' }; renderContact(root); requestAnimationFrame(() => root.querySelector('#me-contact-email')?.focus()); }}>
          ${ic('pencil', 'w-3.5 h-3.5')}</button>` : ''}
      </div>`;

  // Migration pending banner — shown in view mode when admin approval is awaited.
  const migrationBanner = !canEdit ? '' : pending_login_email
    ? html`<div class="sheet__migrate-banner">
        ${ic('clock', 'w-3.5 h-3.5')}
        <span>Login migration to <b>${pending_login_email}</b> is pending admin approval.</span>
        <button type="button" class="sheet__migrate-cancel" @click=${() => cancelMigrationRequest(root)}>Cancel</button>
      </div>`
    : '';

  render(html`${head}
    <div class="sheet__contact">
      ${mobileRow}
      ${emailRow}
    </div>
    ${error && !isEditingMobile && !isEditingEmail ? html`<p class="sheet__contact-err" role="alert">${error}</p>` : ''}
    ${migrationBanner}`, box);
}

function mobileEdit(root) {
  const { mobile, saving, error } = CONTACT;
  const cancel = () => { CONTACT = { ...CONTACT, editField: null, error: '' }; renderContact(root); };
  const save = async () => {
    if (CONTACT.saving) return;
    const input = root.querySelector('#me-mobile');
    const raw = input ? input.value : '';
    const v = normaliseAuPhone(raw);
    if (!v.ok) { CONTACT = { ...CONTACT, error: /** @type {{ ok: false, message: string }} */ (v).message }; renderContact(root); requestAnimationFrame(() => root.querySelector('#me-mobile')?.focus()); return; }
    CONTACT = { ...CONTACT, saving: true, error: '' };
    renderContact(root);
    try {
      const saved = await api.patch('/api/me/profile', { mobile: v.value });
      CONTACT = { ...CONTACT, mobile: saved.mobile ?? v.value, saving: false, editField: null, error: '' };
      renderContact(root);
    } catch (err) {
      CONTACT = { ...CONTACT, saving: false, error: err?.message || 'Couldn\'t save — try again.' };
      renderContact(root);
      requestAnimationFrame(() => root.querySelector('#me-mobile')?.focus());
    }
  };
  return html`
    <form class="sheet__contact-edit" @submit=${(e) => { e.preventDefault(); save(); }}>
      <label class="sheet__contact-edit-label" for="me-mobile">Mobile number</label>
      <input id="me-mobile" type="tel" inputmode="tel" autocomplete="tel-national" class="sheet__contact-input"
             .value=${mobile || ''} placeholder="0412 345 678" aria-describedby="me-mobile-hint" ?disabled=${saving}>
      ${error ? html`<p class="sheet__contact-err" role="alert">${error}</p>`
        : html`<p class="sheet__contact-hint" id="me-mobile-hint">Australian mobile or landline. Leave blank to remove.</p>`}
      <div class="sheet__contact-actions">
        <button type="button" class="btn btn-outline" ?disabled=${saving} @click=${cancel}>Cancel</button>
        <button type="submit" class="btn btn-primary" ?disabled=${saving}>${saving ? 'Saving…' : 'Save'}</button>
      </div>
    </form>`;
}

function contactEmailEdit(root) {
  const { contact_email, login_email, pending_login_email, saving, error, _draft } = CONTACT;
  const currentVal = _draft !== null ? _draft : (contact_email || '');
  const cancel = () => { CONTACT = { ...CONTACT, editField: null, error: '', _draft: null }; renderContact(root); };
  const save = async () => {
    if (CONTACT.saving) return;
    const input = root.querySelector('#me-contact-email');
    const raw = (input ? input.value : '').trim().toLowerCase() || null;
    if (raw && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
      CONTACT = { ...CONTACT, error: 'Enter a valid email address.' };
      renderContact(root);
      requestAnimationFrame(() => root.querySelector('#me-contact-email')?.focus());
      return;
    }
    CONTACT = { ...CONTACT, saving: true, error: '' };
    renderContact(root);
    try {
      const saved = await api.patch('/api/me/profile', { contact_email: raw });
      CONTACT = { ...CONTACT, contact_email: saved.contact_email ?? raw, pending_login_email: saved.pending_login_email ?? null, saving: false, editField: null, error: '', _draft: null };
      renderContact(root);
    } catch (err) {
      CONTACT = { ...CONTACT, saving: false, error: err?.message || "Couldn't save — try again." };
      renderContact(root);
      requestAnimationFrame(() => root.querySelector('#me-contact-email')?.focus());
    }
  };
  // Show migration request button when the draft email is valid, different from the login email,
  // and no request is already pending.
  const draftEmail = currentVal.trim().toLowerCase();
  const draftValid = draftEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draftEmail);
  const canRequestMigration = draftValid && draftEmail !== login_email && !pending_login_email;
  return html`
    <form class="sheet__contact-edit" @submit=${(e) => { e.preventDefault(); save(); }}>
      <label class="sheet__contact-edit-label" for="me-contact-email">Notification email</label>
      <input id="me-contact-email" type="email" inputmode="email" autocomplete="email" class="sheet__contact-input"
             .value=${currentVal} placeholder="alternative@email.com" aria-describedby="me-contact-email-hint" ?disabled=${saving}
             @input=${(e) => { CONTACT = { ...CONTACT, _draft: e.target.value }; renderContact(root); }}>
      ${error ? html`<p class="sheet__contact-err" role="alert">${error}</p>`
        : html`<p class="sheet__contact-hint" id="me-contact-email-hint">Session reminders go here instead of your login email. Leave blank to use your login email.</p>`}
      <div class="sheet__contact-actions">
        <button type="button" class="btn btn-outline" ?disabled=${saving} @click=${cancel}>Cancel</button>
        <button type="submit" class="btn btn-primary" ?disabled=${saving}>${saving ? 'Saving…' : 'Save'}</button>
      </div>
      ${canRequestMigration ? html`
        <div style="margin-top:0.75rem;padding-top:0.75rem;border-top:1px dashed hsl(var(--border));">
          <p class="sheet__contact-hint" style="margin-bottom:0.4rem;">Want to <b>log in</b> with ${draftEmail} instead?</p>
          <button type="button" class="btn btn-destructive" style="width:100%;min-height:2.75rem;" ?disabled=${saving}
              @click=${(e) => { e.preventDefault(); openLoginMigrationConfirm(root, draftEmail); }}>
            ${ic('arrow-right-circle', 'w-4 h-4')} Change login email
          </button>
        </div>` : pending_login_email ? html`
        <p class="sheet__contact-hint" style="margin-top:0.6rem;">
          ${ic('clock', 'w-3 h-3')} Migration to <b>${pending_login_email}</b> pending admin approval.
          <button type="button" class="sheet__migrate-cancel" style="display:inline-block;margin-left:0.3rem;" @click=${(e) => { e.preventDefault(); cancelMigrationRequest(root); }}>Cancel</button>
        </p>` : ''}
    </form>`;
}

// State for the login migration confirm modal (separate from CONTACT to avoid re-render loops)
let _migrationModal = null;

function openLoginMigrationConfirm(root, email) {
  _migrationModal = { email, busy: false, error: '' };
  renderLoginMigrationModal(root);
}

function renderLoginMigrationModal(root) {
  let overlay = root.querySelector('[data-migration-modal]');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.setAttribute('data-migration-modal', '');
    root.appendChild(overlay);
  }
  if (!_migrationModal) { render(html``, overlay); return; }
  const close = () => { _migrationModal = null; renderLoginMigrationModal(root); };
  const confirm = async () => {
    if (_migrationModal.busy) return;
    const email = _migrationModal.email;
    _migrationModal = { ..._migrationModal, busy: true, error: '' };
    renderLoginMigrationModal(root);
    try {
      // First save the contact_email, then submit the migration request
      await api.patch('/api/me/profile', { contact_email: email });
      const res = await api.post('/api/me/email-change-request', {});
      CONTACT = { ...CONTACT, contact_email: email, pending_login_email: res.pending_login_email || email, editField: null, _draft: null, saving: false };
      _migrationModal = null;
      renderLoginMigrationModal(root);
      renderContact(root);
    } catch (err) {
      _migrationModal = { ..._migrationModal, busy: false, error: err?.message || "Request failed — try again." };
      renderLoginMigrationModal(root);
    }
  };
  const { email, busy, error } = _migrationModal;
  render(html`
    <div class="modal-backdrop is-open" data-esc-close @click=${(e) => { if (e.target.classList.contains('modal-backdrop')) close(); }}>
      <div class="modal" style="max-width:28rem;">
        <div class="modal-head">
          <h3>${ic('arrow-right-circle', 'w-5 h-5')} Change login email?</h3>
          <p>Requesting admin approval to move your login to <b>${email}</b>.</p>
        </div>
        <div class="modal-body">
          <p class="text-sm text-muted-foreground">Session reminders will go to <b>${email}</b> right away. Your login email changes only after an admin approves the request.</p>
          ${error ? html`<p class="text-sm font-semibold" style="color:#991B1B;margin-top:0.5rem;">${error}</p>` : ''}
        </div>
        <div class="modal-foot">
          <div class="flex-1"></div>
          <button type="button" class="btn btn-ghost btn-sm" ?disabled=${busy} @click=${close}>Cancel</button>
          <button type="button" class="btn btn-destructive btn-sm" ?disabled=${busy} @click=${confirm}>
            ${busy ? ic('loader', 'w-4 h-4 spinner inline') : ic('check', 'w-4 h-4')} Request change
          </button>
        </div>
      </div>
    </div>`, overlay);
}

async function cancelMigrationRequest(root) {
  if (CONTACT.saving) return;
  CONTACT = { ...CONTACT, saving: true };
  renderContact(root);
  try {
    await api.del('/api/me/email-change-request');
    CONTACT = { ...CONTACT, saving: false, pending_login_email: null };
    renderContact(root);
  } catch (err) {
    CONTACT = { ...CONTACT, saving: false, error: err?.message || 'Couldn\'t cancel — try again.' };
    renderContact(root);
  }
}

/** Inline error + retry — re-runs the whole lazy load (re-fetches all sections). */
function loadError(root, msg) {
  return html`
    <div class="sheet__retry">
      <p class="sheet__hint">${msg}</p>
      <button type="button" class="cal-regen" @click=${() => loadProfileExtras(root)}>
        ${ic('refresh-cw', 'w-3.5 h-3.5')} Try again
      </button>
    </div>`;
}

// ── Roles & currency ──────────────────────────────────────────────────────
const CHIP = { current: 'Current', 'renews-soon': 'Renews soon', lapsed: 'Lapsed' };
function renderRoles(box, roles) {
  if (!box) return;
  if (!roles.length) {
    render(html`<p class="sheet__hint">No roles on file. If that looks wrong, tap “Something wrong?”.</p>`, box);
    return;
  }
  render(html`
    <ul class="sheet__roles">
      ${roles.map((r) => html`
        <li class="sheet__role ${r.status === 'lapsed' ? 'is-lapsed' : r.status === 'renews-soon' ? 'is-renews-soon' : ''}">
          <span class="sheet__role-icon" aria-hidden="true">${ic(r.status === 'lapsed' ? 'alert-octagon' : 'badge-check')}</span>
          <div class="sheet__role-body">
            <div class="sheet__role-head">
              <span class="sheet__role-name">${r.name}</span>
              <span class="sheet__role-chip">${CHIP[r.status] || 'Current'}</span>
            </div>
            <div class="sheet__role-meta">${r.meta}${r.expiry_line ? html`<br>${r.expiry_line}` : ''}</div>
          </div>
        </li>`)}
    </ul>`, box);
}

// ── Season commitment ─────────────────────────────────────────────────────
function renderSeason(root, c) {
  const el = root.querySelector('[data-season-summary]');
  if (!el) return;
  if (!c || !c.sessions) {
    render(html`No sessions yet this season — <strong>sign up</strong> to get started.`, el);
    return;
  }
  render(html`<strong>${c.sessions} session${c.sessions === 1 ? '' : 's'}</strong> this season · ${c.paid} paid · ${c.volunteer} volunteer · <strong>${c.hours} hour${c.hours === 1 ? '' : 's'}</strong>`, el);
}

// ── Calendar sync ─────────────────────────────────────────────────────────
// Subscribing to an external ICS feed has no reliable one-click web deep link:
//   - Apple: webcal:// IS the native "Subscribe to calendar" trigger (iOS/macOS).
//     On a non-Apple desktop it does nothing, so we also copy the URL + show a hint.
//   - Google: the calendar/r?cid=<feed> deep link is unreliable for external ICS —
//     Google often answers "Unable to add calendar, check the URL" even for a valid
//     feed. The path that actually works (user-confirmed) is Settings ▸ Add by URL,
//     so the button copies the link and opens that exact dialog to paste into.
const GOOGLE_ADD_BY_URL = 'https://calendar.google.com/calendar/u/0/r/settings/addbyurl';

function setCalHint(box, msg) {
  const el = box.querySelector('[data-cal-hint]');
  if (el) { el.textContent = msg; el.classList.add('is-shown'); }
}
async function copyToClipboard(text) {
  try { await navigator.clipboard.writeText(text || ''); return true; } catch { return false; }
}

function renderCalendar(box, data, opts = {}) {
  if (!box) return;
  const { regenArmed = false, regenBusy = false, regenErr = '' } = opts;
  const feed = data.feed_url || '';

  const flashCopied = (btn, label = 'Copy') => {
    btn.classList.add('is-copied');
    const span = btn.querySelector('[data-copy-label]');
    if (span) span.textContent = 'Copied';
    setTimeout(() => { btn.classList.remove('is-copied'); if (span) span.textContent = label; }, 1600);
  };
  const onApple = async () => {                       // webcal navigation still fires on Apple devices
    await copyToClipboard(feed);
    setCalHint(box, 'Opening Calendar… if nothing happens, the link is copied — add it via Calendar ▸ File ▸ New Calendar Subscription.');
  };
  const onGoogle = async () => {
    await copyToClipboard(feed);
    window.open(GOOGLE_ADD_BY_URL, '_blank', 'noopener');
    setCalHint(box, 'Link copied — paste it into “URL of calendar” in the Google tab, then Add calendar.');
  };
  const onCopy = async (e) => { const btn = e.currentTarget; await copyToClipboard(feed); flashCopied(btn); };
  const onRegen = async () => {
    if (!regenArmed) { renderCalendar(box, data, { regenArmed: true }); return; }
    renderCalendar(box, data, { regenBusy: true });
    try { renderCalendar(box, { ...data, ...(await api.post('/api/me/calendar/regenerate')) }); }
    catch { renderCalendar(box, data, { regenErr: "Couldn't regenerate — try again" }); }
  };

  render(html`
    <div class="cal-actions">
      <a class="btn btn-outline cal-btn" href=${data.webcal_url || '#'} @click=${onApple}>${ic('calendar', 'w-4 h-4')} Apple Calendar</a>
      <button type="button" class="btn btn-outline cal-btn" @click=${onGoogle}>${ic('calendar-plus', 'w-4 h-4')} Google Calendar</button>
    </div>
    <p class="cal-hint" data-cal-hint></p>
    <div class="cal-url">
      <input type="text" readonly .value=${feed} data-cal-url aria-label="Your private calendar feed URL" @focus=${(e) => e.target.select()}>
      <button type="button" class="btn btn-outline cal-btn" data-cal-copy @click=${onCopy}>${ic('copy-plus', 'w-4 h-4')} <span data-copy-label>Copy</span></button>
    </div>
    <button type="button" class="cal-regen ${regenArmed ? 'is-armed' : ''}" data-cal-regen ?disabled=${regenBusy} @click=${onRegen}>
      ${ic('refresh-cw', 'w-3.5 h-3.5')} <span data-regen-label>${regenErr || (regenBusy ? 'Regenerating…' : regenArmed ? 'Confirm — old links stop working' : 'Regenerate link')}</span>
    </button>
  `, box);
}

// ── Email & notifications ─────────────────────────────────────────────────
function renderNotifications(box) {
  if (!box) return;
  const offsets = NOTIF.reminder_offsets || [];
  const enabled = NOTIF.notify_enabled;
  const me = $session.get();
  const isAdmin = !!(me?.is_admin);

  // Events are bound through lit (@click/@change) — NOT re-attached imperatively
  // after each render. The old querySelectorAll().forEach(addEventListener) added a
  // fresh listener on every render, and since lit reuses the same DOM nodes the
  // handlers stacked up: one click fired saveNotif N times → the toggle flipped on
  // then straight back off ("flickers, can't turn back on"). lit de-dupes @-bindings.
  const fire = (field, checked) => saveNotif(box, { [field]: !checked });
  const toggle = (field, title, checked) => html`
    <button type="button" class="sheet__toggle" role="switch" aria-checked=${checked ? 'true' : 'false'}
            data-notif=${field} aria-label=${title}
      @click=${() => fire(field, checked)}
      @keydown=${(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); fire(field, checked); } }}></button>`;

  // Single-channel row (one toggle, one label)
  const prefRow = (field, title, sub, checked) => html`
    <div class="sheet__pref-row">
      <div class="sheet__pref-label">${title}<small>${sub}</small></div>
      ${toggle(field, title, checked)}
    </div>`;

  // Tristate segmented control: Email | Email & In-App | In-App.
  // emailField=null means email is FORCE-ON (locked); only Email | Email & In-App shown.
  // Derives current state from the two booleans; saves both fields on selection.
  const tristate = (label, emailField, emailChecked, inappField, inappChecked) => {
    const locked = emailField === null;
    // Resolve current state → one of 'email' | 'both' | 'inapp'.
    let state;
    if (locked) {
      state = inappChecked ? 'both' : 'email';
    } else {
      if (emailChecked && inappChecked) state = 'both';
      else if (!emailChecked && inappChecked) state = 'inapp';
      else state = 'email';
    }
    const set = (val) => {
      const patch = {};
      if (!locked && emailField) patch[emailField] = val === 'email' || val === 'both';
      if (inappField) patch[inappField] = val === 'both' || val === 'inapp';
      saveNotif(box, patch);
    };
    const opts = locked
      ? [
          { val: 'email', node: html`${ic('mail', 'w-3 h-3')} Email` },
          { val: 'both',  node: html`${ic('mail', 'w-3 h-3')} & ${ic('bell', 'w-3 h-3')} In-App` },
        ]
      : [
          { val: 'email', node: html`${ic('mail', 'w-3 h-3')} Email` },
          { val: 'both',  node: html`Email & In-App` },
          { val: 'inapp', node: html`${ic('bell', 'w-3 h-3')} In-App` },
        ];
    return html`
      <div class="sheet__tristate" role="group" aria-label="${label} — notification channel">
        ${opts.map((o) => html`
          <button type="button"
            class="sheet__tristate-opt ${state === o.val ? 'is-active' : ''}"
            aria-pressed=${state === o.val ? 'true' : 'false'}
            @click=${() => set(o.val)}>${o.node}</button>`)}
      </div>`;
  };

  const dualRow = (title, sub, emailField, emailChecked, inappField, inappChecked) => html`
    <div class="sheet__pref-row sheet__pref-row--dual">
      <div class="sheet__pref-label">${title}<small>${sub}</small></div>
      ${tristate(title, emailField, emailChecked, inappField, inappChecked)}
    </div>`;

  const lockRow = (title, sub) => html`
    <div class="sheet__pref-row is-locked">
      <div class="sheet__pref-label">${title}<small>${sub}</small></div>
      <span class="sheet__pref-lock" aria-hidden="true" title="Always on">${ic('lock')}</span>
    </div>`;

  const toggleOffset = (m, on) => saveNotif(box, {
    reminder_offsets: on ? offsets.filter((o) => o !== m) : [...offsets, m],
  });

  render(html`
    <div class="sheet__pref-group">
      ${prefRow('notify_enabled', 'Session reminders', "Email before sessions you're on", enabled)}
      ${enabled ? html`
        <div class="sheet__pref-sub">
          <p class="sheet__pref-sub-label">Remind me before each session</p>
          <div class="rem-presets">
            ${REMINDER_PRESETS.map((p) => {
              const on = offsets.includes(p.m);
              return html`<label class="rem-chip ${on ? 'is-on' : ''}">
                <input type="checkbox" data-rem-offset=${p.m} ?checked=${on}
                  @change=${() => toggleOffset(p.m, on)}>
                <span class="rem-chip__check" aria-hidden="true">${ic('check', 'w-3 h-3')}</span>
                <span>${p.label}</span>
              </label>`;
            })}
          </div>
          ${offsets.length === 0 ? html`
            <p class="rem-nudge">${ic('alert-circle')} Pick at least one lead time, or you won't get reminders.</p>` : ''}
        </div>` : ''}
    </div>
    ${prefRow('nudges_enabled', 'Recruitment & help requests', 'Emails asking you to cover sessions', NOTIF.nudges_enabled)}
    ${dualRow(
      'Waitlist spot alerts',
      'When a spot opens on a session you waitlisted',
      'spot_alerts_enabled', NOTIF.spot_alerts_enabled,
      'spot_alerts_inapp_enabled', NOTIF.spot_alerts_inapp_enabled,
    )}
    ${dualRow(
      'Allocation decisions',
      'When your interest in a session is accepted or declined',
      null, true,   /* email is FORCE-ON — locked */
      'interest_alerts_inapp_enabled', NOTIF.interest_alerts_inapp_enabled,
    )}
    ${isAdmin ? dualRow(
      'Admin: new interest received',
      'When a trainer registers interest — admin only',
      'admin_interest_email_enabled', NOTIF.admin_interest_email_enabled,
      'admin_interest_inapp_enabled', NOTIF.admin_interest_inapp_enabled,
    ) : ''}
    ${isPushSupported() ? html`
      <div class="sheet__pref-row">
        <div class="sheet__pref-label">Push notifications
          <small>${isIOS && !isInstalled
            ? 'Add to Home Screen to enable push on iPhone'
            : (typeof Notification !== 'undefined' && Notification.permission === 'denied')
              ? 'Blocked in browser — reset in browser settings'
              : 'Get notified even when the app is closed'}</small>
        </div>
        <button type="button" class="sheet__toggle"
          role="switch"
          aria-checked=${NOTIF.push_notifications_enabled ? 'true' : 'false'}
          aria-label="Push notifications"
          ?disabled=${typeof Notification !== 'undefined' && Notification.permission === 'denied'}
          @click=${() => handlePushToggle(box, NOTIF.push_notifications_enabled)}
          @keydown=${(/** @type {KeyboardEvent} */ e) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              handlePushToggle(box, NOTIF.push_notifications_enabled);
            }
          }}></button>
      </div>` : ''}
    <div class="sheet__pref-divider">Always on</div>
    ${lockRow('Account & session emails', 'Sign-in, confirmations, cancellations, attendance — always sent')}
    ${lockRow('New-device sign-in alerts', 'Security — always on')}
  `, box);
}

/**
 * Handle the push toggle (ADR 20260601_WebPush_PushNotifications_v1).
 * Special handler because toggling push involves the browser permission API and
 * SW subscription — not just a PATCH to /api/me/notifications.
 *
 *  ON  (currentlyEnabled=false):
 *    → call Notification.requestPermission() + subscribe + PATCH enabled=true
 *  OFF (currentlyEnabled=true):
 *    → unsubscribe SW subscription + PATCH enabled=false
 *
 * Must be called from a user gesture (button click) — required by iOS/Chrome.
 */
async function handlePushToggle(box, currentlyEnabled) {
  if (currentlyEnabled) {
    // Disable: optimistic update first
    NOTIF = { ...NOTIF, push_notifications_enabled: false };
    renderNotifications(box);
    await unsubscribePush();
  } else {
    // Enable: request permission → subscribe → enable pref
    if (!isPushSupported()) return;
    if (isIOS && !isInstalled) {
      // iOS in-browser: push requires install first; show install guide
      const { offerInstall } = await import('../lib/push-permission.js');
      offerInstall();
      return;
    }
    const sub = await requestAndSubscribe();
    const enabled = sub !== null;
    NOTIF = { ...NOTIF, push_notifications_enabled: enabled };
    renderNotifications(box);
  }
}

async function saveNotif(box, patch) {
  NOTIF = { ...NOTIF, ...patch };
  renderNotifications(box);                       // optimistic
  try {
    const saved = await api.patch('/api/me/notifications', patch);
    // Sync the full returned prefs (server is authoritative; only the changed
    // fields are sent in `patch`, but the server returns the complete shape).
    NOTIF = {
      notify_enabled: saved.notify_enabled !== false,
      reminder_offsets: saved.reminder_offsets || [],
      nudges_enabled: saved.nudges_enabled !== false,
      spot_alerts_enabled: saved.spot_alerts_enabled !== false,
      spot_alerts_inapp_enabled: saved.spot_alerts_inapp_enabled !== false,
      interest_alerts_inapp_enabled: saved.interest_alerts_inapp_enabled !== false,
      admin_interest_email_enabled: saved.admin_interest_email_enabled !== false,
      admin_interest_inapp_enabled: saved.admin_interest_inapp_enabled !== false,
      push_notifications_enabled: saved.push_notifications_enabled !== false,
    };
    renderNotifications(box);
  } catch { /* leave optimistic; reopen re-syncs */ }
}

// ── Self soft-delete ──────────────────────────────────────────────────────
function wireOffboard(sheet) {
  const modal = sheet.querySelector('[data-offboard-modal]');
  const nameInput = sheet.querySelector('[data-offboard-name]');
  const errEl = sheet.querySelector('[data-offboard-err]');
  const confirmBtn = sheet.querySelector('[data-offboard-confirm]');
  if (!modal) return;
  const showErr = (m) => { if (errEl) errEl.textContent = m || ''; };
  const openModal = () => { showErr(''); if (nameInput) nameInput.value = ''; modal.classList.add('is-open'); nameInput?.focus(); };
  const closeModal = () => modal.classList.remove('is-open');
  sheet.querySelector('[data-offboard-open]')?.addEventListener('click', openModal);
  sheet.querySelector('[data-offboard-cancel]')?.addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });
  confirmBtn?.addEventListener('click', async () => {
    showErr('');
    confirmBtn.disabled = true;
    try {
      await api.post('/api/me/offboard', { confirm_name: nameInput?.value || '' });
      location.href = '/auth';
    } catch (err) {
      confirmBtn.disabled = false;
      showErr(err?.data?.error?.message || err?.message || "Couldn't deactivate. Try again.");
    }
  });
}

export function openProfileSheet(root) { root.querySelector('#profile-sheet')?._open?.(); }
export function closeProfileSheet(root) { root.querySelector('#profile-sheet')?._close?.(); }
